// Classification logic ported 1:1 from the ARM Gold bot's original
// portal.html (district/meal/view detection tuned against real vendor data
// across Violet, Mawasim, Anjum and BillBed) - these internal codes are also
// matched against real backend data (zone names, filter values) so they stay
// as-is. Only DISPLAY TEXT is localized here, via the i18n key maps below -
// consumed through $t() in the components, never rendered as raw strings.

export const DIST_ORDER = [
  'برج الساعة',
  'جبل عمر',
  'جبل الكعبة',
  'اجياد',
  'النسيم',
  'العزيزية',
  'أخرى',
];

// Emoji icons are language-neutral, kept here. Label/description text lives
// in the HOTEL_PORTAL.DISTRICTS i18n block (both locales) and is looked up
// by this same code via districtI18nKeys().
export const DIST_ICONS = {
  'برج الساعة': '🕰',
  'جبل عمر': '⛰',
  'جبل الكعبة': '🕋',
  اجياد: '🏨',
  النسيم: '🌸',
  العزيزية: '🏙',
  أخرى: '📍',
};

const DIST_I18N_CODE = {
  'برج الساعة': 'CLOCK_TOWER',
  'جبل عمر': 'JABAL_OMAR',
  'جبل الكعبة': 'JABAL_KAABA',
  اجياد: 'AJYAD',
  النسيم: 'NASEEM',
  العزيزية: 'AZIZIYAH',
  أخرى: 'OTHER',
};

export function districtI18nKeys(code) {
  const key = DIST_I18N_CODE[code] || 'OTHER';
  return {
    label: `HOTEL_PORTAL.DISTRICTS.${key}`,
    desc: `HOTEL_PORTAL.DISTRICTS.${key}_DESC`,
  };
}

export function mealCat(meal) {
  const m = (meal || '').toLowerCase();
  if (/إفطار قاري|continental|cb|إفطار/.test(m) || m.includes('breakfast') || /\bbb\b/.test(m)) {
    return 'Breakfast';
  }
  if (/نصف إقامة|إفطار وعشاء|half|hb|hf/.test(m)) return 'Half Board';
  if (/ثلاث وجبات|إقامة كاملة|full board|fb/.test(m)) return 'Full Board';
  if (/شامل|all inclusive|ai/.test(m)) return 'All Inclusive';
  return 'Room only';
}

const MEAL_STYLE_CLASS = {
  Breakfast: 'bb',
  'Half Board': 'hb',
  'Full Board': 'fb',
  'All Inclusive': 'ai',
  'Room only': 'ro',
};

const MEAL_I18N_KEY = {
  Breakfast: 'HOTEL_PORTAL.MEALS.BREAKFAST',
  'Half Board': 'HOTEL_PORTAL.MEALS.HALF_BOARD',
  'Full Board': 'HOTEL_PORTAL.MEALS.FULL_BOARD',
  'All Inclusive': 'HOTEL_PORTAL.MEALS.ALL_INCLUSIVE',
  'Room only': 'HOTEL_PORTAL.MEALS.ROOM_ONLY',
};

export function mealInfo(meal) {
  const cat = mealCat(meal);
  return { cat, cls: MEAL_STYLE_CLASS[cat], i18nKey: MEAL_I18N_KEY[cat] };
}

export function getDistrict(name, vendorDistrict) {
  const n = (name || '').toLowerCase();
  const d = (vendorDistrict || '').toLowerCase();
  const t = `${n} ${d}`;
  if (
    /برج الساعه|برج الساعة|ابراج البيت|abraj|clock tower|fairmont.*mak|raffles.*mak|swissotel.*mak|swissôtel.*mak|hilton.*suites.*mak|hilton.*tower.*mak|makkah hotel.*tower|برج مكة/i.test(
      t
    )
  ) {
    return 'برج الساعة';
  }
  if (/جبل عمر|jabal omar|hyatt.*(mak|مك)|movenpick.*jabal|mövenpick.*jabal|kempinski.*jabal/i.test(t)) {
    return 'جبل عمر';
  }
  if (/جبل الكعبه|جبل الكعبة|jabal.*kab/i.test(t)) return 'جبل الكعبة';
  if (
    /اجياد|ajyad|إبراهيم الخليل|ibrahim.*khalil|pullman.*(mak|مك)|sofitel.*(mak|مك)|emaar.*(مك|مك)|marriott.*(mak|مك)|novotel.*(mak|مك)/i.test(
      t
    )
  ) {
    return 'اجياد';
  }
  if (/نسيم|naseem/i.test(t)) return 'النسيم';
  if (/عزيزيه|عزيزية|aziziy/i.test(t)) return 'العزيزية';
  if (d) {
    if (/برج|clock|abraj/i.test(d)) return 'برج الساعة';
    if (/جبل عمر|jabal omar/i.test(d)) return 'جبل عمر';
    if (/جبل الكعب|kab/i.test(d)) return 'جبل الكعبة';
    if (/اجياد|ajyad|ابراهيم|ibrahim/i.test(d)) return 'اجياد';
    if (/نسيم|naseem/i.test(d)) return 'النسيم';
    if (/عزيزيه|عزيزية|aziziy/i.test(d)) return 'العزيزية';
  }
  return null;
}

export function getView(roomType, hotelName) {
  const r = (roomType || '').toLowerCase();
  const h = (hotelName || '').toLowerCase();
  if (/haram|holy|kaaba|كعبة|حرم|مطل.*حرم|مشرف.*حرم/i.test(r)) return 'haram';
  if (/city.*view|cityview|مدينة|مطل.*مدينة/i.test(r)) return 'city';
  if (/mountain|جبل|hill/i.test(r)) return 'mountain';
  if (/pool.*view|مسبح/i.test(r)) return 'pool';
  if (/kaaba.*view|haram.*view|holy.*view|كعبة.*إطلالة|إطلالة.*كعبة|إطلالة.*حرم|حرم.*إطلالة/i.test(h)) return 'haram';
  if (/city.*view|إطلالة.*مدينة|مدينة.*إطلالة/i.test(h)) return 'city';
  return null;
}

export const VIEW_I18N_KEY = {
  haram: 'HOTEL_PORTAL.VIEWS.HARAM',
  city: 'HOTEL_PORTAL.VIEWS.CITY',
  mountain: 'HOTEL_PORTAL.VIEWS.MOUNTAIN',
  pool: 'HOTEL_PORTAL.VIEWS.POOL',
};

// `source` values are the real backend field (from bot.rb's connectors) and
// must stay exactly as-is for filtering/matching; only the display label is
// localized (Violet/BillBed are brand names so EN/AR share them; Mawasim/
// Anjum get real Arabic trade names in the AR locale).
export const SOURCE_META = {
  'Violet B2B': { key: 'vi', dot: '🟣', i18nKey: 'HOTEL_PORTAL.SOURCES.VIOLET' },
  Mawasim: { key: 'ma', dot: '🟢', i18nKey: 'HOTEL_PORTAL.SOURCES.MAWASIM' },
  Anjum: { key: 'an', dot: '🟠', i18nKey: 'HOTEL_PORTAL.SOURCES.ANJUM' },
  BillBed: { key: 'bb', dot: '🔵', i18nKey: 'HOTEL_PORTAL.SOURCES.BILLBED' },
};

// Prices are Saudi Riyal amounts read by staff regardless of UI language -
// always render Latin digits (numberingSystem: 'latn') so a value like 1,150
// never silently becomes Eastern Arabic numerals (١١٥٠) when the UI is
// switched to Arabic, matching how the rest of Chatwoot displays numbers.
export function fP(n) {
  return Number(n || 0).toLocaleString('en-US', {
    maximumFractionDigits: 0,
    numberingSystem: 'latn',
  });
}

export function fN(n) {
  return Number(n || 0).toLocaleString('en-US', { numberingSystem: 'latn' });
}
